import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { reportsExtendedApi } from "../../services/reports-extended.api";
import { useReportData } from "../../hooks/useReportData";
import { apiClient } from "@/lib/apiClient";
import {
  Employees,
  BirthdayCake,
  EmployeeManagement,
  EmployeeDemographics,
} from "@/components/icons";
import styles from "./PersonnelDemographicsReportPage.module.scss";

const PAGE_SIZE = 20;
const BASE = "/api/admin/reports";

// ---------- Types ----------
export interface DemographicsDeptItem {
  departmentID: string;
  name: string;
  userCount: number;
  avgAge: number;
  avgTenureYears: number;
  malePercent: number;
  femalePercent: number;
}

export interface DemographicsBand {
  label: string;
  count: number;
}

export interface DemographicsResponse {
  summary: {
    totalUsers: number;
    activeUsers: number;
    avgAge: number;
    avgTenureYears: number;
    malePercent: number;
    femalePercent: number;
    unknownGenderPercent: number;
  };
  byAgeBand: DemographicsBand[];
  byTenureBand: DemographicsBand[];
  byGender: DemographicsBand[];
  byLocation: DemographicsBand[];
  items: DemographicsDeptItem[];
  count: number;
}

export interface DemographicsParams {
  page?: number;
  limit?: number;
  search?: string;
  departmentID?: string;
}

// ---------- Local fetcher (TransformResponseInterceptor unwrap) ----------
function unwrap<T>(body: { data?: T } | T): T {
  if (body && typeof body === "object" && "data" in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const fetchDemographics = async (
  params: DemographicsParams,
  signal?: AbortSignal,
): Promise<DemographicsResponse> => {
  const { data } = await apiClient.get(`${BASE}/personnel-demographics`, {
    params,
    signal,
  });
  return unwrap<DemographicsResponse>(data);
};

// ---------- Helpers ----------
const formatNumber = (v: number) =>
  new Intl.NumberFormat("tr-TR").format(Math.round(v));

const formatPercent = (v: number) => `${(v ?? 0).toFixed(1)}%`;

const formatYears = (v: number) => `${(v ?? 0).toFixed(1)}`;

const PersonnelDemographicsReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const params: DemographicsParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: filter.search?.trim() || undefined,
      departmentID: filter.departmentID || undefined,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    DemographicsResponse,
    DemographicsParams
  >({
    fetcher: (p, signal) => fetchDemographics(p, signal),
    params,
  });

  const summary = data?.summary;
  const byAgeBand = data?.byAgeBand ?? [];
  const byTenureBand = data?.byTenureBand ?? [];
  const byGender = data?.byGender ?? [];
  const byLocation = data?.byLocation ?? [];
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topLocations = [...byLocation]
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const columns: TableColumn<DemographicsDeptItem>[] = [
    {
      header: t("reports.personnelDemographics.headers.unit"),
      accessor: (row) => (
        <span className={styles.titleCell}>
          {row.name || "—"}
        </span>
      ),
    },
    {
      header: t("reports.personnelDemographics.headers.staff"),
      accessor: (row) => (
        <span className={styles.numCell}>{formatNumber(row.userCount)}</span>
      ),
    },
    {
      header: t("reports.personnelDemographics.headers.avgAge"),
      accessor: (row) => (
        <span className={styles.numCell}>{formatYears(row.avgAge)}</span>
      ),
    },
    {
      header: t("reports.personnelDemographics.headers.avgTenure"),
      accessor: (row) => (
        <span className={styles.numCell}>{formatYears(row.avgTenureYears)}</span>
      ),
    },
    {
      header: t("reports.personnelDemographics.headers.malePct"),
      accessor: (row) => (
        <span className={styles.malePct}>{formatPercent(row.malePercent)}</span>
      ),
    },
    {
      header: t("reports.personnelDemographics.headers.femalePct"),
      accessor: (row) => (
        <span className={styles.femalePct}>
          {formatPercent(row.femalePercent)}
        </span>
      ),
    },
  ];

  return (
    <ReportDetailLayout
      title={t("reports.personnelDemographics.title")}
      categoryLabel={t("reports.personnelDemographics.categoryLabel")}
      description={t("reports.personnelDemographics.description")}
      exportSlot={
        <ReportExportButtons
          reportType="PERSONNEL_DEMOGRAPHICS"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="personel-demografi"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDateRange={false}
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title={t("reports.personnelDemographics.kpi.totalUsers")}
          value={summary ? formatNumber(summary.totalUsers) : "—"}
          icon={<Employees width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.personnelDemographics.kpi.activeUsers")}
          value={summary ? formatNumber(summary.activeUsers) : "—"}
          icon={<EmployeeManagement width={18} height={18} />}
          accent="success"
          footnote={t("reports.personnelDemographics.kpi.activeUsersNote")}
        />
        <ReportKPICard
          title={t("reports.personnelDemographics.kpi.avgAge")}
          value={summary ? formatYears(summary.avgAge) : "—"}
          icon={<BirthdayCake width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.personnelDemographics.kpi.avgTenure")}
          value={summary ? formatYears(summary.avgTenureYears) : "—"}
          icon={<EmployeeDemographics width={18} height={18} />}
          accent="warning"
        />
      </div>

      <div className={styles.chartGrid}>
        <ReportChart
          title={t("reports.personnelDemographics.charts.ageBand")}
          type="bar"
          isLoading={isLoading}
          isEmpty={byAgeBand.length === 0}
          height={280}
          series={[
            {
              name: t("reports.personnelDemographics.charts.userCount"),
              data: byAgeBand.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: byAgeBand.map((b) => b.label) },
            colors: ["#4f46e5"],
            plotOptions: {
              bar: { borderRadius: 6, columnWidth: "55%" },
            },
            legend: { show: false },
          }}
        />

        <ReportChart
          title={t("reports.personnelDemographics.charts.tenureBand")}
          type="bar"
          isLoading={isLoading}
          isEmpty={byTenureBand.length === 0}
          height={280}
          series={[
            {
              name: t("reports.personnelDemographics.charts.userCount"),
              data: byTenureBand.map((b) => b.count),
            },
          ]}
          options={{
            xaxis: { categories: byTenureBand.map((b) => b.label) },
            colors: ["#10b981"],
            plotOptions: {
              bar: { borderRadius: 6, columnWidth: "55%" },
            },
            legend: { show: false },
          }}
        />

        <ReportChart
          title={t("reports.personnelDemographics.charts.gender")}
          type="donut"
          isLoading={isLoading}
          isEmpty={byGender.length === 0}
          height={280}
          series={byGender.map((g) => g.count)}
          options={{
            labels: byGender.map((g) => g.label),
            colors: ["#2563eb", "#db2777", "#94a3b8"],
            legend: { position: "bottom" },
            dataLabels: { enabled: true, formatter: (val: number) => `${val.toFixed(0)}%` },
          }}
        />
      </div>

      <div className={styles.chartGridFull}>
        <ReportChart
          title={t("reports.personnelDemographics.charts.locationTop10")}
          subtitle={t("reports.personnelDemographics.charts.locationSubtitle")}
          type="treemap"
          isLoading={isLoading}
          isEmpty={topLocations.length === 0}
          height={320}
          series={[
            {
              data: topLocations.map((l) => ({
                x: l.label,
                y: l.count,
              })),
            },
          ]}
          options={{
            colors: ["#4f46e5"],
            legend: { show: false },
            plotOptions: {
              treemap: {
                distributed: true,
                enableShades: true,
              },
            },
          }}
        />
      </div>

      <Table
        columns={columns}
        data={items}
        isLoading={isLoading}
        contentBasedHeight
        getRowId={(row) => row.departmentID ?? row.name}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.personnelDemographics.noData")
            : t("reports.personnelDemographics.noItems")
        }
      />
    </ReportDetailLayout>
  );
};

export default PersonnelDemographicsReportPage;
